/* eslint-disable no-underscore-dangle */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import MDButton from "components/MDButton";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { protectedResources, siteData } from "../../authConfig";

export default function DelegationDialog({
  open,
  onClose,
  category,
  taxonomyId,
  userEmail,
  isAdmin,
  isCategoryOwner,
  onSaved,
}) {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const [delegates, setDelegates] = useState([]);
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newDelegateEmail, setNewDelegateEmail] = useState("");
  const [useFullScope, setUseFullScope] = useState(true);
  const [newSubCategoryIds, setNewSubCategoryIds] = useState([]);
  const fetched = useRef(false);

  const base = protectedResources?.apiTodoList?.endpoint || "";
  const categoryId = category?.id || category?._id;
  const canManage =
    isAdmin ||
    isCategoryOwner ||
    (category?.Owner || "").toLowerCase() === (userEmail || "").toLowerCase();

  const loadDelegates = () => {
    if (!base || !taxonomyId || !categoryId) return;
    execute("GET", `${base}/delegates?taxonomyId=${taxonomyId}&categoryId=${categoryId}`)
      .then((res) => {
        if (res?.success && Array.isArray(res.data)) setDelegates(res.data);
      })
      .catch(() => setDelegates([]));
  };

  useEffect(() => {
    if (!open) {
      fetched.current = false;
      return;
    }
    if (open && taxonomyId && categoryId) {
      loadDelegates();
      if (!fetched.current) {
        fetched.current = true;
        execute("GET", `${base}/users/list-for-owners`).then((res) => {
          if (res?.data && Array.isArray(res.data)) setOwnerUsers(res.data);
        });
      }
    }
  }, [open, taxonomyId, categoryId, execute]);

  const handleAdd = () => {
    if (!newDelegateEmail?.trim()) return;
    if (!useFullScope && newSubCategoryIds.length === 0) return;
    execute("POST", `${base}/delegates`, {
      taxonomyId,
      categoryId,
      delegatedTo: newDelegateEmail.trim(),
      subCategoryIds: useFullScope ? [] : newSubCategoryIds,
    }).then((res) => {
      if (res?.success) {
        setNewDelegateEmail("");
        setNewSubCategoryIds([]);
        setAddOpen(false);
        loadDelegates();
        onSaved?.();
      }
    });
  };

  const handleDelete = (delegateId) => {
    execute("DELETE", `${base}/delegates/${delegateId}`).then((res) => {
      if (res?.success) {
        loadDelegates();
        onSaved?.();
      }
    });
  };

  const toggleSubCategory = (subId) => {
    setNewSubCategoryIds((prev) =>
      prev.includes(subId) ? prev.filter((id) => id !== subId) : [...prev, subId]
    );
  };

  const subCategories = category?.SubCategories || [];
  const fullScope = newSubCategoryIds.length === 0;
  const canAdd = newDelegateEmail?.trim() && (fullScope || newSubCategoryIds.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage delegates - {category?.name}</DialogTitle>
      <DialogContent sx={{ minHeight: 120 }}>
        {!canManage ? (
          <p style={{ color: "#333", margin: "16px 0" }}>
            Only the category owner or a Risk Admin can manage delegates.
          </p>
        ) : (
          <>
            {delegates.length === 0 && !addOpen && (
              <p style={{ color: "#666", fontSize: "14px", margin: "0 0 12px 0" }}>
                No delegates assigned yet. Add one below to allow another user to act on this
                category.
              </p>
            )}
            <List dense>
              {delegates.map((d) => (
                <ListItem key={d.id || d._id}>
                  <ListItemText
                    primary={d.delegatedTo}
                    secondary={
                      d.subCategoryIds?.length
                        ? `Subcategories: ${d.subCategoryIds.length}`
                        : "Full category"
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleDelete(d.id || d._id)} size="small">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            {addOpen ? (
              <div style={{ marginTop: 16 }}>
                <FormControl fullWidth margin="dense" sx={{ minHeight: 48 }}>
                  <InputLabel>Delegate (user)</InputLabel>
                  <Select
                    value={newDelegateEmail}
                    label="Delegate (user)"
                    onChange={(e) => setNewDelegateEmail(e.target.value)}
                    sx={{ minHeight: 48 }}
                  >
                    <MenuItem value="">
                      <em>Select user</em>
                    </MenuItem>
                    {ownerUsers
                      .filter(
                        (u) => (u.email || "").toLowerCase() !== (userEmail || "").toLowerCase()
                      )
                      .map((u) => (
                        <MenuItem key={u.id || u.email} value={(u.email || "").toLowerCase()}>
                          {u.name || u.email} ({u.email})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="dense" size="small" component="fieldset">
                  <InputLabel shrink>Scope</InputLabel>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={useFullScope}
                          onChange={(e) => {
                            setUseFullScope(e.target.checked);
                            if (e.target.checked) setNewSubCategoryIds([]);
                          }}
                        />
                      }
                      label="Full category (all subcategories)"
                    />
                    {!useFullScope &&
                      subCategories.map((sub, idx) => (
                        <FormControlLabel
                          key={sub?.id || idx}
                          control={
                            <Checkbox
                              checked={newSubCategoryIds.includes(sub?.id)}
                              onChange={() => toggleSubCategory(sub?.id)}
                            />
                          }
                          label={sub.name}
                        />
                      ))}
                  </FormGroup>
                </FormControl>
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <MDButton
                    variant="contained"
                    size="small"
                    onClick={handleAdd}
                    disabled={!canAdd}
                    sx={{
                      backgroundColor: "#402899",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#352080" },
                      "&.Mui-disabled": { backgroundColor: "#ccc", color: "#666" },
                    }}
                  >
                    Add
                  </MDButton>
                  <Button size="small" onClick={() => setAddOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <MDButton
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
                sx={{
                  mt: 1,
                  backgroundColor: "#402899",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#352080" },
                }}
              >
                Add delegate
              </MDButton>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
